import useDispatch from '@hooks/useDispatch'
import useForm from '@hooks/useForm'
import type { UseFormOptions } from '@hooks/useForm'
import useLatestEntity from '@hooks/useLatestEntity'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import defaultFormState, { requiredFields } from '@models/adAccountGroup'

import type { AdAccountGroupModel, AdAccountGroupRequestOptions } from '@models/types'

import * as adAccountGroupActions from '@redux/modules/adAccountGroup'

import type { AppDispatch } from '@redux/store'
import type { ModuleState } from '@redux/modules/types'

export const generateUrls = (adAccountGroup?: Partial<AdAccountGroupModel>) => {
  const { id } = adAccountGroup || {}

  return {
    adAccountGroupsIndexUrl: '#/adAccountGroups',
    editAdAccountGroupRelationsUrl: `#/adAccountGroups/${id}/edit/relations`,
    editAdAccountGroupUrl: `#/adAccountGroups/${id}/edit/`,
  }
}

type CreateAdAccountGroupParams = {
  adAccountGroupParams: Partial<AdAccountGroupModel>,
  dispatch: AppDispatch,
  requestOptions?: AdAccountGroupRequestOptions,
}

const createAdAccountGroup = (params: CreateAdAccountGroupParams) => {
  const { adAccountGroupParams, dispatch, requestOptions } = params
  const { createAdAccountGroup: createFn } = adAccountGroupActions

  return dispatch(createFn(adAccountGroupParams, requestOptions)).then((response) => {
    const { success } = response

    if (success) {
      const { data } = response

      const { editAdAccountGroupUrl } = generateUrls(data)

      return {
        success,
        data,
        redirectUrl: editAdAccountGroupUrl,
      }
    }

    return response
  })
}

type UpdateAdAccountGroupParams = {
  adAccountGroup: AdAccountGroupModel,
  adAccountGroupParams: Partial<AdAccountGroupModel>,
  dispatch: AppDispatch,
  requestOptions?: AdAccountGroupRequestOptions,
}

const updateAdAccountGroup = (params: UpdateAdAccountGroupParams) => {
  const { adAccountGroup, adAccountGroupParams, dispatch, requestOptions } = params
  const { updateAdAccountGroup: updateFn } = adAccountGroupActions

  const updatedParams = {
    id: adAccountGroup.id,
    ...adAccountGroupParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

export function useAdAccountGroupForm(
  adAccountGroup: Partial<AdAccountGroupModel>,
  options: UseFormOptions = {},
) {
  const { validateOn } = options

  const adAccountGroupForm = useForm(
    defaultFormState,
    { entity: adAccountGroup, requiredFields, validateOn },
    [adAccountGroup.id, adAccountGroup.cacheKey],
  )

  return {
    ...adAccountGroupForm,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: AdAccountGroupRequestOptions,
}

function useAdAccountGroup(initEntity: Partial<AdAccountGroupModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  const { entity: adAccountGroup }: { entity: AdAccountGroupModel } = useLatestEntity(initEntity, 'adAccountGroups')
  const { id } = adAccountGroup

  const dispatch = useDispatch()

  useReduxAction(
    'adAccountGroups',
    'loadAdAccountGroup',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const { creating, loading, updating } = useSelector(reduxState => reduxState.adAccountGroups)

  return {
    adAccountGroup,
    callbacks: {
      createAdAccountGroup: (adAccountGroupParams: Partial<AdAccountGroupModel>, entityOptions?: AdAccountGroupRequestOptions) => (
        createAdAccountGroup({ adAccountGroupParams, dispatch, requestOptions: entityOptions })
      ),
      updateAdAccountGroup: (adAccountGroupParams: Partial<AdAccountGroupModel>, entityOptions?: AdAccountGroupRequestOptions) => (
        updateAdAccountGroup({ adAccountGroup, adAccountGroupParams, dispatch, requestOptions: entityOptions })
      ),
    },
    creating,
    loading,
    updating,
    urls: generateUrls(adAccountGroup),
  }
}

export default useAdAccountGroup
